import { useMemo } from 'react';
import { generateStretchablePinVariants } from '../utils/svgDefinitions/createTrailLengthPinSvg';
import createTrailPinSvg from '../utils/svgDefinitions/createTrailPinSvg';
import {
  multiTrailPinActiveImageNameBase,
  multiTrailPinClosedActiveImageNameBase,
  multiTrailPinClosedImageNameBase,
  multiTrailPinCompletedActiveImageNameBase,
  multiTrailPinCompletedImageNameBase,
  multiTrailPinImageNameBase,
  multiTrailPinSavedActiveImageNameBase,
  multiTrailPinSavedImageNameBase,
  trailPinActiveImageName,
  trailPinClosedActiveImageName,
  trailPinClosedImageName,
  trailPinCollapsedClosedImageName,
  trailPinCollapsedCompletedImageName,
  trailPinCollapsedImageName,
  trailPinCollapsedSavedImageName,
  trailPinCollapsedViewedImageName,
  trailPinCompletedActiveImageName,
  trailPinCompletedImageName,
  trailPinImageName,
  trailPinSavedActiveImageName,
  trailPinSavedImageName
} from '../utils/trailPinConstants';
import { SvgDefinition } from '../utils/useSvgImages';

type Args = {
  trailCounts: Set<number>;
  closedTrailCounts: Set<number>;
  savedTrailCounts: Set<number>;
  completedTrailCounts: Set<number>;
  hasNewPinsExperiment: boolean;
};

export default function useTrailPinSvgDefinitions({
  trailCounts,
  closedTrailCounts,
  savedTrailCounts,
  completedTrailCounts,
  hasNewPinsExperiment
}: Args) {
  const svgDefinitions = useMemo(() => {
    const definitions: SvgDefinition[] = [];

    definitions.push(
      createTrailPinSvg({ imageName: trailPinCollapsedImageName, trailCount: 1, isCollapsed: true, hasNewPinsExperiment }),
      createTrailPinSvg({
        imageName: trailPinCollapsedClosedImageName,
        trailCount: 1,
        isClosed: true,
        isCollapsed: true,
        hasNewPinsExperiment
      }),
      createTrailPinSvg({
        imageName: trailPinCollapsedSavedImageName,
        trailCount: 1,
        isSaved: true,
        isCollapsed: true,
        hasNewPinsExperiment
      }),
      createTrailPinSvg({
        imageName: trailPinCollapsedCompletedImageName,
        trailCount: 1,
        isCompleted: true,
        isCollapsed: true,
        hasNewPinsExperiment
      })
    );

    if (hasNewPinsExperiment) {
      definitions.push(
        createTrailPinSvg({
          imageName: trailPinCollapsedViewedImageName,
          trailCount: 1,
          isCollapsed: true,
          hasNewPinsExperiment: true,
          isViewed: true
        })
      );
      // Generate stretchable pill-shaped pin SVGs (only 12 total: 3 states × 4 types)
      // These use icon-text-fit to dynamically stretch to fit text content
      definitions.push(...generateStretchablePinVariants());
    } else {
      definitions.push(
        createTrailPinSvg({ imageName: trailPinImageName, trailCount: 1 }),
        createTrailPinSvg({ imageName: trailPinActiveImageName, trailCount: 1, isActive: true }),
        createTrailPinSvg({ imageName: trailPinClosedImageName, trailCount: 1, isClosed: true }),
        createTrailPinSvg({ imageName: trailPinClosedActiveImageName, trailCount: 1, isActive: true, isClosed: true }),
        createTrailPinSvg({ imageName: trailPinSavedImageName, trailCount: 1, isSaved: true }),
        createTrailPinSvg({ imageName: trailPinSavedActiveImageName, trailCount: 1, isActive: true, isSaved: true }),
        createTrailPinSvg({ imageName: trailPinCompletedImageName, trailCount: 1, isCompleted: true }),
        createTrailPinSvg({ imageName: trailPinCompletedActiveImageName, trailCount: 1, isActive: true, isCompleted: true })
      );
      trailCounts.forEach(count => {
        definitions.push(
          createTrailPinSvg({ imageName: `${multiTrailPinImageNameBase}${count}`, trailCount: count }),
          createTrailPinSvg({ imageName: `${multiTrailPinActiveImageNameBase}${count}`, trailCount: count, isActive: true })
        );
      });
      closedTrailCounts.forEach(count => {
        definitions.push(
          createTrailPinSvg({ imageName: `${multiTrailPinClosedImageNameBase}${count}`, trailCount: count, isClosed: true }),
          createTrailPinSvg({
            imageName: `${multiTrailPinClosedActiveImageNameBase}${count}`,
            trailCount: count,
            isActive: true,
            isClosed: true
          })
        );
      });
      savedTrailCounts.forEach(count => {
        definitions.push(
          createTrailPinSvg({ imageName: `${multiTrailPinSavedImageNameBase}${count}`, trailCount: count, isSaved: true }),
          createTrailPinSvg({
            imageName: `${multiTrailPinSavedActiveImageNameBase}${count}`,
            trailCount: count,
            isActive: true,
            isSaved: true
          })
        );
      });
      completedTrailCounts.forEach(count => {
        definitions.push(
          createTrailPinSvg({ imageName: `${multiTrailPinCompletedImageNameBase}${count}`, trailCount: count, isCompleted: true }),
          createTrailPinSvg({
            imageName: `${multiTrailPinCompletedActiveImageNameBase}${count}`,
            trailCount: count,
            isActive: true,
            isCompleted: true
          })
        );
      });
    }

    return definitions;
  }, [closedTrailCounts, completedTrailCounts, hasNewPinsExperiment, savedTrailCounts, trailCounts]);

  return svgDefinitions;
}
